import { showErrorMessage, showSuccessMessage } from 'components/Helpers/Toasts';
import { getUserFriendlyErrors } from 'connectors/lib/friendlyErrorMessageParser';
import { CartFragmentApi, useApplyPromoCodeToCartMutationApi } from 'graphql/generated';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useCallback, useState } from 'react';
import { usePersistStore } from 'store/usePersistStore';

export type ApplyPromocodeHandler = (
    newPromoCode: string,
    messages: { success: string; error: string },
) => Promise<CartFragmentApi | null | undefined>;

export const useApplyPromoCodeToCart = (): [ApplyPromocodeHandler, boolean, boolean] => {
    const [{ fetching }, applyPromoCodeToCart] = useApplyPromoCodeToCartMutationApi();
    const { cartUuid, defaultStoreUuid } = usePersistStore((s) => s);
    const t = useTypedTranslationFunction();
    const [isValid, setIsValid] = useState(true);

    const applyPromoCodeHandler = useCallback<ApplyPromocodeHandler>(
        async (newPromoCode, messages) => {
            const applyPromoCodeResult = await applyPromoCodeToCart({
                input: { promoCode: newPromoCode, cartUuid },
                defaultStoreUuid,
                selectedStoreUuid: null,
            });

            // EXTEND PROMO CODE MODIFICATIONS HERE

            const PROMO_CODE_ERROR_MAPPING = new Map([
                ['AIRME_PROMO_CODE_BLOCKED', t('AIRME_PROMO_CODE_BLOCKED')],
                ['AIRME_PROMO_CODE_BLOCKED_BY_OTHER_TRANSACTION', t('AIRME_PROMO_CODE_BLOCKED_BY_OTHER_TRANSACTION')],
                ['AIRME_PROMO_CODE_EXPIRED', t('AIRME_PROMO_CODE_EXPIRED')],
                ['AIRME_PROMO_CODE_INVALID_CURRENCY', t('AIRME_PROMO_CODE_INVALID_CURRENCY')],
                ['AIRME_PROMO_CODE_NOT_FOUND', t('AIRME_PROMO_CODE_NOT_FOUND')],
                ['AIRME_PROMO_CODE_NOT_VALID_FOR_LOCATION', t('AIRME_PROMO_CODE_NOT_VALID_FOR_LOCATION')],
                ['AIRME_PROMO_CODE_NOT_VALID_FOR_PRODUCTS', t('AIRME_PROMO_CODE_NOT_VALID_FOR_PRODUCTS')],
                ['AIRME_PROMO_CODE_UNKNOWN_ERROR', t('AIRME_PROMO_CODE_UNKNOWN_ERROR')],
                ['AIRME_PROMO_CODE_USED', t('AIRME_PROMO_CODE_USED')],
                ['AIRME_PROMO_CODE_CANNOT_BE_APPLIED', t('AIRME_PROMO_CODE_CANNOT_BE_APPLIED')],
                ['AIRME_PROMO_CODE_NOT_VALID_GIFT_VOUCHER', t('AIRME_PROMO_CODE_NOT_VALID_GIFT_VOUCHER')],
                ['AIRME_PROMO_CODE_NOT_VALID', t('AIRME_PROMO_CODE_NOT_VALID')],
                ['PROMO_CODE_ALREADY_APPLIED', t('PROMO_CODE_ALREADY_APPLIED')],
                ['PROMO_CODE_ALREADY_APPLIED_DISCOUNT_COUPON', t('PROMO_CODE_ALREADY_APPLIED_DISCOUNT_COUPON')],
                ['PROMO_CODE_AT_MOST_ONE_PERCENTAGE', t('PROMO_CODE_AT_MOST_ONE_PERCENTAGE')],
                ['AIRME_PROMO_CODE_BLOCKED_AND_REMOVED', t('AIRME_PROMO_CODE_BLOCKED_AND_REMOVED')],
                [
                    'AIRME_PROMO_CODE_BLOCKED_BY_OTHER_TRANSACTION_AND_REMOVED',
                    t('AIRME_PROMO_CODE_BLOCKED_BY_OTHER_TRANSACTION_AND_REMOVED'),
                ],
                ['AIRME_PROMO_CODE_EXPIRED_AND_REMOVED', t('AIRME_PROMO_CODE_EXPIRED_AND_REMOVED')],
                ['AIRME_PROMO_CODE_INVALID_CURRENCY_AND_REMOVED', t('AIRME_PROMO_CODE_INVALID_CURRENCY_AND_REMOVED')],
                ['AIRME_PROMO_CODE_NOT_FOUND_AND_REMOVED', t('AIRME_PROMO_CODE_NOT_FOUND_AND_REMOVED')],
                [
                    'AIRME_PROMO_CODE_NOT_VALID_FOR_LOCATION_AND_REMOVED',
                    t('AIRME_PROMO_CODE_NOT_VALID_FOR_LOCATION_AND_REMOVED'),
                ],
                [
                    'AIRME_PROMO_CODE_NOT_VALID_FOR_PRODUCTS_AND_REMOVED',
                    t('AIRME_PROMO_CODE_NOT_VALID_FOR_PRODUCTS_AND_REMOVED'),
                ],
                ['AIRME_PROMO_CODE_CANNOT_BE_APPLIED_AND_REMOVED', t('AIRME_PROMO_CODE_CANNOT_BE_APPLIED_AND_REMOVED')],
                [
                    'AIRME_PROMO_CODE_NOT_VALID_GIFT_VOUCHER_AND_REMOVED',
                    t('AIRME_PROMO_CODE_NOT_VALID_GIFT_VOUCHER_AND_REMOVED'),
                ],
                [
                    'GIFT_VOUCHER_NOT_APPLICABLE_FOR_PERSONAL_PICKUP',
                    t('GIFT_VOUCHER_NOT_APPLICABLE_FOR_PERSONAL_PICKUP'),
                ],
                ['GIFT_CODE_CANNOT_BE_APPLIED_WITH_RESERVATION', t('GIFT_CODE_CANNOT_BE_APPLIED_WITH_RESERVATION')],
                [
                    'GIFT_CODE_CANNOT_BE_APPLIED_WITH_RESERVATION_AND_REMOVED',
                    t('GIFT_CODE_CANNOT_BE_APPLIED_WITH_RESERVATION_AND_REMOVED'),
                ],
                ['AIRME_PROMO_CODE_MINIMUM_ORDER_PRICE_NOT_MET', t('AIRME_PROMO_CODE_MINIMUM_ORDER_PRICE_NOT_MET')],
                ['GIFT_CODE_CANNOT_BE_APPLIED_ON_CART_ITEMS', t('GIFT_CODE_CANNOT_BE_APPLIED_ON_CART_ITEMS')],
            ]);

            setIsValid(!applyPromoCodeResult.error);

            if (applyPromoCodeResult.error !== undefined) {
                const { userError, applicationError } = getUserFriendlyErrors(applyPromoCodeResult.error, t);

                if (userError?.validation?.promoCode !== undefined) {
                    if (userError.validation.promoCode.code === 'AIRME_PROMO_CODE_MINIMUM_ORDER_PRICE_NOT_MET') {
                        showErrorMessage(userError.validation.promoCode.message, 'cart');
                    } else if (
                        userError.validation.promoCode.code?.startsWith('AIRME_') ||
                        userError.validation.promoCode.code?.startsWith('GIFT_CODE_') ||
                        userError.validation.promoCode.code?.startsWith('GIFT_VOUCHER_')
                    ) {
                        showErrorMessage(
                            PROMO_CODE_ERROR_MAPPING.get(userError.validation.promoCode.code) ??
                                t('Something went wrong.'),
                            'cart',
                        );
                    } else {
                        showErrorMessage(userError.validation.promoCode.message, 'cart');
                    }
                } else {
                    if (applicationError !== undefined) {
                        showErrorMessage(applicationError.message, 'cart');
                    } else {
                        showErrorMessage(messages.error, 'cart');
                    }
                }

                return null;
            }

            showSuccessMessage(messages.success);

            return applyPromoCodeResult.data?.ApplyPromoCodeToCart;
        },
        [applyPromoCodeToCart, cartUuid, defaultStoreUuid, t],
    );

    return [applyPromoCodeHandler, fetching, isValid];
};
